import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  CircularProgress,
  Skeleton,
  Tooltip,
  Alert,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import Language from "@mui/icons-material/Language";
import { RefreshCw, Gauge } from "lucide-react";

import { Button } from "../../../components/Button";
import { Card, CardContent } from "../../../components/Card";
import { CustomTextField } from "../../../components/CustomTextField";
import { useTheme as useCustomTheme } from "../../../theme/useTheme";
import { EmptyStateMessage } from "../../../components/EmptyStateMessage";
import { ScoreGauge, scoreColor } from "./ScoreGauge";
import { SCORE_CARDS, STRATEGIES } from "./webPerformance.js";
import { useTaskPolling } from "../../../hooks/useTaskPolling.js";
import {
  queuePageSpeedAudit,
  getPageSpeedSnapshot,
} from "../../../services/metricsService";

// Two per row on phones, one line on wide screens; grid (not wrapping flex)
// so every card in a row keeps the same width.
const SCORE_GRID_SX = {
  display: "grid",
  gridTemplateColumns: {
    xs: "repeat(2, minmax(0, 1fr))",
    md: "repeat(4, minmax(0, 1fr))",
  },
  gap: 2,
};

const VITALS_GRID_SX = {
  display: "grid",
  gridTemplateColumns: {
    xs: "repeat(2, minmax(0, 1fr))",
    sm: "repeat(3, minmax(0, 1fr))",
    xl: "repeat(6, minmax(0, 1fr))",
  },
  gap: 2,
};

const VITALS = [
  {
    key: "largest_contentful_paint",
    label: "LCP",
    help: "Largest Contentful Paint: cuánto tarda en pintarse el elemento principal.",
  },
  {
    key: "total_blocking_time",
    label: "TBT",
    help: "Total Blocking Time: tiempo en que el hilo principal queda bloqueado.",
  },
  {
    key: "cumulative_layout_shift",
    label: "CLS",
    help: "Cumulative Layout Shift: cuánto se mueve el contenido al cargar.",
  },
  {
    key: "first_contentful_paint",
    label: "FCP",
    help: "First Contentful Paint: primer contenido visible.",
  },
  {
    key: "speed_index",
    label: "Speed Index",
    help: "Velocidad con la que el contenido se muestra visualmente.",
  },
  {
    key: "time_to_interactive",
    label: "TTI",
    help: "Time To Interactive: cuándo la página responde de forma fiable.",
  },
];

const formatMs = (value) => {
  if (value === null || value === undefined) return "—";
  if (value >= 1000) return `${(value / 1000).toFixed(1)} s`;
  return `${Math.round(value)} ms`;
};

// A Lighthouse run takes 20-60 seconds; poll a bit beyond that before giving up.
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_ATTEMPTS = 60;

// Maps a failed request to a message that makes sense to a consultant.
const userFacingError = (err) => {
  const status = err?.response?.status;
  if (status === 400) {
    return "No se puede analizar: revisa que el cliente tenga una web válida configurada.";
  }
  if (status === 502) {
    return "El servicio de análisis no está disponible ahora mismo. Vuelve a intentarlo en unos minutos.";
  }
  if (status === 403 || status === 404) {
    return "No tienes acceso a las métricas de este cliente.";
  }
  return "No se ha podido ejecutar el análisis. Vuelve a intentarlo.";
};

function VitalCard({ label, help, metric }) {
  const theme = useTheme();
  // Lighthouse ships a localized display value; fall back to the raw number.
  const text = metric?.display || formatMs(metric?.value);

  return (
    <Tooltip title={help} arrow>
      <Box
        sx={{
          minWidth: 0,
          padding: 2,
          borderRadius: "12px",
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "1.15rem",
            mt: 0.5,
            color: scoreColor(
              metric?.score === null || metric?.score === undefined
                ? undefined
                : metric.score * 100
            ),
          }}
        >
          {text}
        </Typography>
      </Box>
    </Tooltip>
  );
}

/** Placeholder in the shape of the four score gauges, the app's loading idiom. */
function ScoresSkeleton() {
  return (
    <Box sx={SCORE_GRID_SX}>
      {SCORE_CARDS.map(({ key }) => (
        <Skeleton key={key} variant="rounded" height={148} sx={{ borderRadius: "12px" }} />
      ))}
    </Box>
  );
}

/** Same for the load-metric cards, so a running audit shows the final layout. */
function VitalsSkeleton() {
  return (
    <Card>
      <CardContent sx={{ padding: { xs: 2, sm: 3 } }}>
        <Skeleton variant="text" width={180} sx={{ mb: 1.5 }} />
        <Box sx={VITALS_GRID_SX}>
          {VITALS.map(({ key }) => (
            <Skeleton key={key} variant="rounded" height={76} sx={{ borderRadius: "12px" }} />
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

/**
 * Device selector. Deliberately not a tab bar: the screen already has tabs for
 * the metric families, and nesting a second tab row inside one reads badly.
 */
function StrategyToggle({ value, onChange }) {
  const { neutrals } = useCustomTheme();

  return (
    <Stack direction="row" spacing={0.75}>
      {STRATEGIES.map(({ value: strategyValue, label, icon: Icon }) => {
        const isActive = strategyValue === value;
        return (
          <Button
            key={strategyValue}
            onClick={() => onChange(strategyValue)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              height: "28px",
              minHeight: "28px",
              padding: "0 12px",
              borderRadius: "999px",
              fontSize: "0.75rem",
              fontWeight: 500,
              whiteSpace: "nowrap",
              backgroundColor: isActive ? neutrals.gray[900] : "transparent",
              color: isActive ? neutrals.white : neutrals.gray[600],
              border: `1px solid ${isActive ? neutrals.gray[900] : neutrals.gray[200]}`,
              "&:hover": {
                backgroundColor: isActive ? neutrals.gray[800] : neutrals.gray[50],
                borderColor: isActive ? neutrals.gray[800] : neutrals.gray[300],
              },
            }}
          >
            <Icon size={13} />
            {label}
          </Button>
        );
      })}
    </Stack>
  );
}

export function WebPerformancePanel({ clientId, website }) {
  const theme = useTheme();
  const [strategy, setStrategy] = useState("mobile");
  // Editable override, precargado con la web del cliente pero libre de cambiar.
  const [urlInput, setUrlInput] = useState(website || "");
  // Audits shown in this visit, per device. Nothing is kept in the browser:
  // what survives leaving the screen is the snapshot the backend stores.
  const [resultsByStrategy, setResultsByStrategy] = useState({
    mobile: null,
    desktop: null,
  });
  const [taskId, setTaskId] = useState(null);
  const [isQueueing, setIsQueueing] = useState(false);
  const [isLoadingSnapshot, setIsLoadingSnapshot] = useState(true);
  const [auditingStrategy, setAuditingStrategy] = useState(null);
  const [error, setError] = useState("");

  // The device can be switched while an audit runs, so the result must be
  // filed under the device it was requested for, not the one shown now.
  const auditedStrategyRef = useRef(strategy);

  // An audit only ever runs because the user asked for it: entering the screen
  // shows the stored snapshot and nothing else.
  const queueAudit = useCallback(
    async (auditStrategy, auditUrl) => {
      setError("");
      setIsQueueing(true);
      auditedStrategyRef.current = auditStrategy;
      setAuditingStrategy(auditStrategy);
      try {
        const { task_id: newTaskId } = await queuePageSpeedAudit(
          clientId,
          auditStrategy,
          auditUrl
        );
        setTaskId(newTaskId);
      } catch (err) {
        console.error(
          "PageSpeed audit could not be queued",
          err?.response?.data || err
        );
        setError(userFacingError(err));
        setAuditingStrategy(null);
      } finally {
        setIsQueueing(false);
      }
    },
    [clientId]
  );

  // On entry the backend is asked for this month's stored audit, which is the
  // last one run during it. Nothing is launched automatically: Lighthouse only
  // runs when the user presses the button.
  useEffect(() => {
    let active = true;

    setResultsByStrategy({ mobile: null, desktop: null });
    setUrlInput(website || "");
    setError("");
    setTaskId(null);
    setAuditingStrategy(null);
    setIsLoadingSnapshot(true);

    (async () => {
      let snapshot = null;
      try {
        snapshot = await getPageSpeedSnapshot(clientId);
      } catch (err) {
        console.error(
          "This month's PageSpeed audit could not be read",
          err?.response?.data || err
        );
      }
      if (!active) return;

      setIsLoadingSnapshot(false);

      // The month keeps one audit per device; both are shown as they are, and
      // the toggle opens on whichever one is stored (mobile if both are).
      const audits = snapshot?.audits || {};
      const stored = {
        mobile: audits.mobile || null,
        desktop: audits.desktop || null,
      };
      setResultsByStrategy(stored);
      if (!stored.mobile && stored.desktop) {
        setStrategy("desktop");
      }
    })();

    return () => {
      active = false;
    };
  }, [clientId, website]);

  const handleTaskSuccess = useCallback(
    (data) => {
      const auditedStrategy = auditedStrategyRef.current;
      setTaskId(null);
      setAuditingStrategy(null);
      if (!data) {
        setError("El análisis ha terminado sin resultados. Vuelve a intentarlo.");
        return;
      }
      setResultsByStrategy((previous) => ({
        ...previous,
        [auditedStrategy]: data,
      }));
    },
    []
  );

  const handleTaskError = useCallback(
    (taskError) => {
      // The worker error text is for developers (missing API key, Google quota,
      // …), so it is logged rather than shown to the user.
      console.error("PageSpeed audit failed", taskError);
      setTaskId(null);
      setAuditingStrategy(null);
      setError(
        "No se ha podido completar el análisis. Vuelve a intentarlo en unos minutos."
      );
    },
    []
  );

  useTaskPolling(taskId, {
    onSuccess: handleTaskSuccess,
    onError: handleTaskError,
    interval: POLL_INTERVAL_MS,
    maxAttempts: POLL_MAX_ATTEMPTS,
  });

  const isRunning = isQueueing || isLoadingSnapshot || Boolean(taskId);
  const result = resultsByStrategy[strategy] || null;

  const handleStrategyChange = (value) => {
    setStrategy(value);
    setError("");
  };

  const trimmedUrl = urlInput.trim();

  const handleRun = () => queueAudit(strategy, trimmedUrl);

  return (
    <Stack spacing={2}>
      <Card>
        <CardContent sx={{ padding: { xs: 2, sm: 3 } }}>
          <Stack spacing={2}>
            <Stack spacing={0.75}>
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                <Language
                  sx={{ fontSize: 18, color: theme.palette.text.secondary }}
                />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Web a analizar
                </Typography>
              </Stack>

              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                sx={{
                  alignItems: { md: "flex-start" },
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ width: "100%", maxWidth: 360 }}>
                  <CustomTextField
                    id="pagespeed-url"
                    type="url"
                    value={urlInput}
                    onChange={(event) => setUrlInput(event.target.value)}
                    placeholder="Web de tu cliente"
                    maxLength={300}
                    isUrlField
                  />
                  {/* The line keeps its space when empty: appearing text would
                      change the row height and shift the toolbar. */}
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      color: theme.palette.text.secondary,
                      mt: 0.5,
                      minHeight: "18px",
                    }}
                  >
                    {result?.fetch_time
                      ? `Análisis del ${new Date(result.fetch_time).toLocaleString("es-ES")}`
                      : ""}
                  </Typography>
                </Box>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  sx={{ alignItems: { sm: "center" }, flexShrink: 0 }}
                >
                  <StrategyToggle value={strategy} onChange={handleStrategyChange} />

                  <Button
                    variant="default"
                    onClick={handleRun}
                    disabled={isRunning || !trimmedUrl}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexShrink: 0,
                    }}
                  >
                    <RefreshCw
                      size={16}
                      style={
                        auditingStrategy === strategy
                          ? {
                              animation: "spin 1s linear infinite",
                              transformOrigin: "center",
                            }
                          : undefined
                      }
                    />
                    {auditingStrategy === strategy
                      ? "Analizando…"
                      : result
                        ? "Volver a analizar"
                        : "Analizar rendimiento"}
                  </Button>
                </Stack>
              </Stack>
            </Stack>

            {error && <Alert severity="error">{error}</Alert>}

            {(isLoadingSnapshot || auditingStrategy === strategy) && <ScoresSkeleton />}

            {!isLoadingSnapshot &&
              !result &&
              auditingStrategy !== strategy &&
              !error && (
                <EmptyStateMessage
                  icon={Gauge}
                  message={
                    trimmedUrl
                      ? `Lanza el análisis para ver el rendimiento en ${
                          strategy === "mobile" ? "móvil" : "ordenador"
                        }.`
                      : "Indica una web para poder analizar el rendimiento."
                  }
                  sx={{ py: 4 }}
                />
              )}

            {result && auditingStrategy !== strategy && (
              <Box sx={SCORE_GRID_SX}>
                {SCORE_CARDS.map(({ key, label }) => (
                  <ScoreGauge
                    key={key}
                    label={label}
                    score={result.scores?.[key]}
                  />
                ))}
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>

      {auditingStrategy === strategy && <VitalsSkeleton />}

      {result && auditingStrategy !== strategy && (
        <Card>
          <CardContent sx={{ padding: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
              Métricas de carga
            </Typography>
            <Box sx={VITALS_GRID_SX}>
              {VITALS.map(({ key, label, help }) => (
                <VitalCard
                  key={key}
                  label={label}
                  help={help}
                  metric={result.metrics?.[key]}
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {result && auditingStrategy !== strategy && result.opportunities?.length > 0 && (
        <Card>
          <CardContent sx={{ padding: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
              Oportunidades de mejora
            </Typography>
            <Stack spacing={1}>
              {result.opportunities.map((item) => (
                <Box
                  key={item.id}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                    padding: 2,
                    borderRadius: "12px",
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.background.paper,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 0 }}>
                    {item.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      color: "#ff4e42",
                    }}
                  >
                    -{formatMs(item.savings_ms)}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
}

export default WebPerformancePanel;
